import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { MenuItem, TextField } from "@mui/material";
import MessageSuccess from "../../../../messages/messageSuccess";
import MessageError from "../../../../messages/messageError";
import FazerObservacoes from "../../../components/fazerObservacoes.";
import api from "../../../../services/api";
import RegisterAccess from "../../../../utils/registerAccess";
import ButtonLeft from "../../../../utils/buttonLeft";
import Delete from "../../../../utils/delete";

export const ESTABELECIMENTO_STATUS_OPTIONS = [
  'Análise',
  // 'Analisado',
  // 'Analisado Chefe de Secção',
  // 'Inspecção Agendada',
  // 'Inspecção Realizada',
  // 'Directora Geral Adjunta para os Serviços Executivos',
  // 'Deferimento',
  // 'Activo',
  // 'Inactivo',
  //   'Por Renovar',
  //   'Processo Alterado',
  'Reprovado',
  'Arquivado',
  // 'Não Licenciado',
];

const DEFAULT_REQUIRE_OBS = ['Reprovado', 'Arquivado', 'Inactivo', 'Não Licenciado', 'Processo Alterado'];

export default function ChangeStatusEstabelecimentoComplexo({
  estabelecimento,
  estabelecimentoId,
  title = 'Alteração complexa de estado',
  description = 'Use este recurso apenas quando a alteração de estado exigir uma decisão manual fora do fluxo padrão.',
  submitLabel = 'Confirmar alteração',
  cancelLabel = 'Cancelar',
  defaultStatus = '',
  allowedStatuses = ESTABELECIMENTO_STATUS_OPTIONS,
  requireObservationFor = DEFAULT_REQUIRE_OBS,
  observationTitle = '(Descreve o motivo desta alteração quando necessário)',
  successAlert = 'Estado alterado com sucesso.',
  registerAccessLabel = 'alteração complexa de estado de estabelecimento',
  enableDelete = true,
  onSuccess,
  onCancel,
  cancelPath,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.account.user);
  const finalEstabelecimentoId = estabelecimentoId || estabelecimento?._id || id;

  const [messageSuccess, setMessageSuccess] = useState('');
  const [messageError, setMessageError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [obs, setObs] = useState('');
  const [targetStatus, setTargetStatus] = useState(defaultStatus || '');

  const requiresObservation = useMemo(
    () => requireObservationFor.includes(targetStatus),
    [requireObservationFor, targetStatus]
  );

  // const statusAtual = (estabelecimento?.status || '').trim().toLowerCase();
  // const canDelete = enableDelete && (statusAtual === 'reprovado' || statusAtual === 'reprovada');

  const canDelete = enableDelete && estabelecimento?.status === 'Reprovado';

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    if (cancelPath) {
      navigate(cancelPath);
      return;
    }

    navigate(localStorage.getItem('urlAnterior') || -1);
  };

  async function createObservacao() {
    if (!obs) return;

    await api.post('/observacao/create', {
      assunto: `Alteração de estado para ${targetStatus}`,
      descricao: obs,
      userId: user._id,
      estabelecimentoId: finalEstabelecimentoId,
    });
  }

  async function handleChangeStatus() {
    if (!targetStatus) {
      setMessageError('Selecione o estado de destino.');
      return;
    }

    if (requiresObservation && !obs.trim()) {
      setMessageError('Necessário inserir observações para este tipo de alteração.');
      return;
    }

    setLoading(true);
    setMessageSuccess('');
    setMessageError('');

    try {
      const response = await api.patch('/estabelecimento/change-status', {
        estabelecimentoId: finalEstabelecimentoId,
        status: targetStatus,
        userId: user._id,
      });

      await createObservacao();

      setMessageSuccess(response?.data?.msg || successAlert);
      dispatch({ type: 'alert', payload: { alert: successAlert } });

      if (onSuccess) {
        onSuccess(targetStatus, response?.data);
      } else {
        window.setTimeout(() => {
          navigate(localStorage.getItem('urlAnterior') || -1);
        }, 3000);
      }
    } catch (error) {
      setMessageError(error?.response?.data?.message || 'Não foi possível alterar o estado do estabelecimento.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-6 tw-shadow-sm tw-mb-4">
      <RegisterAccess page={registerAccessLabel} />

      {messageSuccess && <MessageSuccess message={messageSuccess} />}
      {messageError && <MessageError message={messageError} />}

      <div className="tw-mb-6">
        <h2 className="tw-mb-1 tw-text-lg tw-font-bold tw-text-gray-800">{title}</h2>
        <div className="tw-mb-4 tw-h-px tw-bg-gray-100" />
        <p className="tw-mb-4 tw-text-sm tw-leading-relaxed tw-text-gray-600">{description}</p>

        <div className="tw-grid tw-gap-4 md:tw-grid-cols-[minmax(0,280px)_1fr] md:tw-items-start">
          <div>
            <p className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">Novo estado</p>
            <TextField
              select
              fullWidth
              size="small"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
            >
              <MenuItem value="">Selecionar estado</MenuItem>
              {allowedStatuses.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {allowedStatuses.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTargetStatus(item)}
                className={[
                  'tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition',
                  targetStatus === item
                    ? 'tw-border-[#85287e] tw-bg-[#85287e] tw-text-white'
                    : 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-700 hover:tw-bg-slate-100'
                ].join(' ')}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-mt-5">
          <FazerObservacoes
            setObs={setObs}
            obs={obs}
            titulo={requiresObservation ? `${observationTitle} - obrigatório para ${targetStatus}` : observationTitle}
          />
        </div>

        <ButtonLeft
          title={submitLabel}
          funcao={handleChangeStatus}
          load={loading}
          disabled={loading || !targetStatus}
        >
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="tw-rounded-lg tw-p-2 tw-text-sm tw-font-semibold tw-text-red-500 tw-transition-colors tw-duration-200 hover:tw-text-red-600 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
          >
            {cancelLabel}
          </button>
        </ButtonLeft>

        {canDelete && (
          <div className="tw-mt-5 tw-rounded-lg tw-border tw-border-red-100 tw-bg-red-50 tw-p-4">
            <p className="tw-text-sm tw-font-semibold tw-text-red-700">Zona de risco</p>
            <p className="tw-mt-1 tw-text-xs tw-text-red-600">
              Esta ação remove definitivamente o estabelecimento e deve ser usada apenas em casos excecionais.
            </p>

            <button
              type="button"
              onClick={() => setOpenDelete(true)}
              disabled={loading || !finalEstabelecimentoId}
              className="tw-mt-3 tw-rounded-lg tw-bg-red-600 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition-colors tw-duration-200 hover:tw-bg-red-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              Eliminar estabelecimento
            </button>
          </div>
        )}
      </div>

      <Delete
        open={canDelete && openDelete}
        setOpen={setOpenDelete}
        url={'/estabelecimento/delete'}
        tipo={'Estabelecimento'}
        msg={`Tem certeza que deseja eliminar o estabelecimento ${estabelecimento?.nome || ''}`}
        id={finalEstabelecimentoId}
        userId={user?._id}
      />
    </div>
  );
}